import { useState } from 'react';
import { LayoutGrid, List as ListIcon, Pencil, Trash2, UserPlus, Users } from 'lucide-react';
import type { ProjectDetail } from '../../lib/projectsApi';
import { OrgChart } from '../people/OrgChart';
import { initials } from '../people/UtilizationBar';
import { EditWithAIButton } from '../ui/EditWithAIButton';
import { useConfirm } from '../ui/ConfirmProvider';
import { useI18n } from '../../contexts/I18nContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useRemoveKbProjectMember } from '../../hooks/useKbProject';
import { AddTeamMemberModal } from './AddTeamMemberModal';
import { ApiError } from '../../lib/api';

// ---------------------------------------------------------------------------
// Project → Team tab (CLE-72)
//
// BUG FIX: the previous tab read `project.members` (the legacy ProjectMember
// User<->Project join), which is empty under the markdown-first KB, so it
// always showed "0 members". The team is now driven by `project.team`, derived
// on the backend from Allocations (Person<->Project).
//
// REDESIGN: the team is shown as an org chart (reusing the People/Team
// component from CLE-70), restricted to the people on this project, with a
// list/table toggle as an alternative view.
// ---------------------------------------------------------------------------

type View = 'chart' | 'list';

function Avatar({ name, avatarUrl }: { name: string; avatarUrl: string | null }) {
  if (avatarUrl) {
    return (
      <img src={avatarUrl} alt={name} className="h-8 w-8 shrink-0 rounded-full object-cover" />
    );
  }
  return (
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-600 text-xs font-semibold text-white">
      {initials(name)}
    </div>
  );
}

export function ProjectTeamTab({ project }: { project: ProjectDetail }) {
  const { t } = useI18n();
  const { can } = usePermissions();
  const confirm = useConfirm();
  const { members, orgChart } = project.team;
  const [view, setView] = useState<View>('chart');
  const [showAddModal, setShowAddModal] = useState(false);
  // Email of the member currently being edited (CLE-441), or null when adding.
  const [editEmail, setEditEmail] = useState<string | null>(null);

  const canEditTeam = can('projects', 'canEditTeam');
  const removeMutation = useRemoveKbProjectMember(project.code ?? project.id);
  const removeError =
    removeMutation.error instanceof ApiError
      ? removeMutation.error.message
      : removeMutation.error
        ? t('projects.team.removeFailed')
        : null;

  async function handleRemove(member: { email: string; fullName: string }) {
    if (
      !(await confirm({
        message: t('projects.team.removeConfirm', { name: member.fullName }),
        danger: true,
      }))
    )
      return;
    removeMutation.mutate(member.email);
  }

  function handleEdit(member: { email: string }) {
    setEditEmail(member.email);
    setShowAddModal(false);
  }

  const addButton = canEditTeam ? (
    <button
      type="button"
      onClick={() => setShowAddModal(true)}
      className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-brand-700"
    >
      <UserPlus size={15} /> {t('projects.team.addMember')}
    </button>
  ) : null;

  const modal =
    showAddModal || editEmail ? (
      <AddTeamMemberModal
        project={project}
        editMember={editEmail ? { email: editEmail } : null}
        onClose={() => {
          setShowAddModal(false);
          setEditEmail(null);
        }}
      />
    ) : null;

  if (members.length === 0) {
    return (
      <div>
        <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-8 text-center">
          <Users className="mx-auto h-10 w-10 text-gray-300" />
          <p className="mt-3 text-sm font-medium text-gray-700">{t('projects.team.noMembers')}</p>
          <p className="mt-1 text-sm text-gray-500">{t('projects.team.noMembersHint')}</p>
          {canEditTeam && <div className="mt-4 flex justify-center">{addButton}</div>}
        </div>
        <div className="mt-6">
          <EditWithAIButton
            label={t('projects.team.assignWithAI')}
            prompt={`Vreau să aloc un membru de echipă proiectului „${project.name}”. Te rog întreabă-mă cine și la ce procent, apoi actualizează proiectul în Baza de cunoștințe.`}
          />
        </div>
        {modal}
      </div>
    );
  }

  return (
    <div>
      {/* View toggle */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-gray-600">
          <span className="font-semibold text-gray-900">{members.length}</span>{' '}
          {members.length === 1 ? t('projects.team.member') : t('projects.team.members')}
        </div>
        <div className="flex items-center gap-2">
          {addButton}
          <div className="inline-flex overflow-hidden rounded-md border border-gray-300">
            <button
              type="button"
              onClick={() => setView('chart')}
              aria-label={t('projects.team.orgChart')}
              aria-pressed={view === 'chart'}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium ${
                view === 'chart'
                  ? 'bg-brand-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {/* Icons only on phones: with the labels the toggle ran off the
                  right edge next to "Adaugă membru". */}
              <LayoutGrid size={15} />{' '}
              <span className="hidden sm:inline">{t('projects.team.orgChart')}</span>
            </button>
            <button
              type="button"
              onClick={() => setView('list')}
              aria-label={t('projects.team.list')}
              aria-pressed={view === 'list'}
              className={`inline-flex items-center gap-1.5 border-l border-gray-300 px-3 py-1.5 text-sm font-medium ${
                view === 'list'
                  ? 'bg-brand-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <ListIcon size={15} />{' '}
              <span className="hidden sm:inline">{t('projects.team.list')}</span>
            </button>
          </div>
        </div>
      </div>

      {removeError && (
        <p className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{removeError}</p>
      )}

      {view === 'chart' ? (
        <OrgChart
          data={orgChart}
          onRemoveMember={canEditTeam ? handleRemove : undefined}
          removePending={removeMutation.isPending}
        />
      ) : (
        <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2 font-medium">{t('projects.team.col.name')}</th>
                <th className="px-3 py-2 font-medium">{t('projects.team.col.title')}</th>
                <th className="px-3 py-2 font-medium">{t('projects.team.col.department')}</th>
                <th className="px-3 py-2 font-medium">{t('projects.team.col.allocation')}</th>
                <th className="px-3 py-2 font-medium">{t('projects.team.col.email')}</th>
                {canEditTeam && <th className="px-3 py-2" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {members.map((member) => (
                <tr key={member.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <Avatar name={member.fullName} avatarUrl={member.avatarUrl} />
                      <span className="font-medium text-gray-900">{member.fullName}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-gray-700">{member.title ?? '—'}</td>
                  <td className="px-3 py-2 text-gray-700">{member.department ?? '—'}</td>
                  <td className="px-3 py-2 text-gray-700">
                    {member.allocationHours > 0 ? (
                      <span className="inline-flex items-center rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700">
                        {member.allocationHours}
                        {t('projects.team.hoursPerWeekSuffix')}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">{t('projects.team.inactive')}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-700">{member.email}</td>
                  {canEditTeam && (
                    <td className="px-3 py-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => handleEdit(member)}
                          className="rounded p-1 text-gray-400 transition-colors hover:bg-brand-50 hover:text-brand-600"
                          aria-label={t('projects.team.editMember')}
                          title={t('projects.team.editMember')}
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemove(member)}
                          disabled={removeMutation.isPending}
                          className="rounded p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                          aria-label={t('projects.team.removeMember')}
                          title={t('projects.team.removeMember')}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-6">
        <EditWithAIButton
          label={t('projects.team.assignWithAI')}
          prompt={`Vreau să aloc un membru de echipă proiectului „${project.name}”. Te rog întreabă-mă cine și la ce procent, apoi actualizează proiectul în Baza de cunoștințe.`}
        />
      </div>
      {modal}
    </div>
  );
}
